<template>
  <div class="border-static-default-low rounded-md border p-8">
    <div class="flex items-center gap-8">
      <RevIllustration
        v-if="product.imageUrl"
        alt=""
        class="!size-32"
        :height="32"
        loading="lazy"
        :src="product.imageUrl"
        :width="32"
      />

      <div class="flex flex-col md:gap-4">
        <!-- Mobile -->
        <div
          class="flex w-fit items-center gap-2"
          :class="{ 'md:hidden': !forceMobileDisplay }"
        >
          <div ref="mobileProductRef" class="line-clamp-1 break-all">
            <RevLink
              v-if="canDisplayProductLink"
              class="text-caption-bold"
              rel="nofollow"
              :to="productLink"
              :tracking="productModelTracking"
            >
              {{ product.title }}
            </RevLink>

            <p v-else class="text-static-default-hi caption-bold">
              {{ product.title }}
            </p>
          </div>

          <RevTooltip
            v-if="isMobileTextClamped"
            :modifiers="TOOLTIP_MODIFIER"
            position="top"
          >
            <template #trigger="{ show, hide }">
              <IconInfo
                size="16"
                @click="show"
                @focusin="show"
                @focusout="hide"
                @mouseenter="show"
                @mouseleave="hide"
              />
            </template>

            {{ product.title }}
          </RevTooltip>
        </div>

        <!-- Desktop -->
        <div
          class="hidden w-fit items-center"
          :class="{ 'md:flex': !forceMobileDisplay }"
        >
          <p
            ref="desktopProductRef"
            class="caption-bold line-clamp-1 max-w-[450px] break-all"
          >
            {{ product.title }}
          </p>

          <RevTooltip v-if="isDesktopTextClamped" position="top">
            <template #trigger="{ show, hide }">
              <IconInfo
                :aria-label="i18n(translations.moreDetails)"
                class="ml-2 cursor-pointer"
                size="16"
                @click="show"
                @focusin="show"
                @focusout="hide"
                @mouseenter="show"
                @mouseleave="hide"
              />
            </template>

            {{ product.title }}
          </RevTooltip>

          <RevLink
            v-if="canDisplayProductLink"
            class="text-static-default-low leading-caption text-caption-bold ml-8 flex items-center whitespace-nowrap text-right"
            rel="nofollow"
            :to="productLink"
            :tracking="productModelTracking"
          >
            {{ i18n(translations.seeItem) }}
          </RevLink>
        </div>

        <div
          class="flex flex-col items-start gap-4"
          :class="{
            'md:flex-row  md:items-center md:gap-20': !forceMobileDisplay,
          }"
        >
          <div v-if="gradeData" class="flex items-center">
            <p class="text-static-default-low caption mr-4">
              {{ i18n(translations.productDetailsCondition) }}
            </p>

            <p class="text-action-default-hi caption-bold">
              {{ gradeData.name }}
            </p>

            <RevTooltip class="ml-2" position="top">
              <template #trigger="{ show, hide }">
                <IconInfo
                  :aria-label="i18n(translations.moreDetails)"
                  size="16"
                  @click="show"
                  @focusin="show"
                  @focusout="hide"
                  @mouseenter="show"
                  @mouseleave="hide"
                />
              </template>
              {{ gradeData.description }}
            </RevTooltip>
          </div>

          <div class="flex items-center">
            <p
              class="text-static-default-low caption mr-4"
              data-allow-mismatch="text"
            >
              {{ i18n(translations.purchaseDate) }}
            </p>
            <p
              class="text-action-default-hi caption-bold"
              data-allow-mismatch="text"
            >
              {{
                i18n.date(new Date(product.purchasedAt), { dateStyle: 'short' })
              }}
            </p>
          </div>

          <div v-if="seller && showSeller" class="flex items-center">
            <p class="text-static-default-low caption mr-4">
              {{ i18n(translations.productDetailsSeller) }}
            </p>

            <RevLink
              v-if="sellerRouteExists"
              class="caption-bold"
              :to="sellerLink"
              @click="onTrackClick"
            >
              {{ seller.companyName }}
            </RevLink>

            <p v-else class="caption-bold underline">
              {{ seller.companyName }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRoute, useRouter } from '#imports'
import { computed, ref } from 'vue'

import type { Grade, Link } from '@backmarket/http-api'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'
import { RevIllustration } from '@ds/components/Illustration'
import { RevLink } from '@ds/components/Link'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconInfo } from '@ds/icons/IconInfo'

import type { UserComment } from '../../../models/UserComment.type'

import translations from './ProductDetails.translations'

const props = withDefaults(
  defineProps<{
    backboxGrade: Grade | null
    product: UserComment['product']
    isTranslatable: boolean
    seller?: { companyName: string; companySlug: string; id: string }
    tracking?: Record<string, unknown>
    displaySeller: boolean
    forceMobileDisplay?: boolean
    displayProductLink?: boolean
  }>(),
  {
    forceMobileDisplay: false,
    seller: undefined,
    tracking: undefined,
    displayProductLink: true,
  },
)

const i18n = useI18n()

const TOOLTIP_MODIFIER = [
  {
    name: 'offset',
    options: {
      offset: [-70, 8],
    },
  },
]

const experiments = useExperiments()
const route = useRoute()
const router = useRouter()
const { trackClick } = useTracking()
const { market } = useMarketplace()

const mobileProductRef = ref<HTMLElement | null>(null)
const desktopProductRef = ref<HTMLElement | null>(null)

function routeExists(link: Link | string) {
  try {
    return router.resolve(toNuxtLink(link)).matched.length > 0
  } catch {
    return false
  }
}

const productLink = computed(() => {
  if (!props.isTranslatable) {
    return props.product.link
  }

  const locale = toBcp47Locale(market.defaultLocale)

  if (typeof props.product.link === 'string') {
    return props.product.link.replace(/[a-z]{2}-[a-z]{2}/, locale)
  }

  if ('name' in props.product.link) {
    const { name, params } = props.product.link

    return { name, params: { ...params, locale } }
  }

  return props.product.link
})

const productModelTracking = computed(() => ({
  name: 'PP_link_review',
  page_type: String(route.name),
}))

const showSeller = computed(() => {
  return (
    props.displaySeller &&
    experiments['experiment.reviewSellerAndFilters'] === 'showSeller'
  )
})

const gradeData = computed(() => {
  switch (props.backboxGrade?.name) {
    case 'ECO':
      return {
        name: i18n(translations.ecoGrade),
        description: i18n(translations.ecoGradeDescription),
      }
    case 'VERY_GOOD':
      return {
        name: i18n(translations.veryGoodGrade),
        description: i18n(translations.veryGoodGradeDescription),
      }
    case 'FLAWLESS':
      return {
        name: i18n(translations.flawlessGrade),
        description: i18n(translations.flawlessGradeDescription),
      }
    case 'PREMIUM':
      return {
        name: i18n(translations.premiumGrade),
        description: i18n(translations.premiumGradeDescription),
      }
    default:
      return null
  }
})

function onTrackClick() {
  if (!props.seller) return

  trackClick({
    ...props.tracking,
    name: 'review_seller',
    reviewSellerId: props.seller.id,
  })
}

const sellerLink = computed(() => {
  if (!props?.seller?.companySlug) return undefined

  const locale = toBcp47Locale(market.defaultLocale)

  return `/${locale}/r/s/${props.seller.companySlug}/${props.seller.id}`
})

const sellerRouteExists = computed(() => {
  if (!sellerLink.value) return false

  return routeExists(sellerLink.value)
})

const isMobileTextClamped = computed(() => {
  if (!mobileProductRef.value) return false

  return (
    mobileProductRef.value.scrollHeight > mobileProductRef.value.clientHeight
  )
})

const isDesktopTextClamped = computed(() => {
  if (!desktopProductRef.value) return false

  return (
    desktopProductRef.value.scrollHeight > desktopProductRef.value.clientHeight
  )
})

const canDisplayProductLink = computed(() => {
  return routeExists(props.product.link) && props.displayProductLink
})
</script>
